import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { FiDownload, FiLogOut, FiRefreshCw, FiTrash2 } from "react-icons/fi";
import "./PlanStudioRecovery.css";

export default function PlanStudioRecovery({ recovery, onRetry, onClear, onLogout, projectName = 'Riomax' }) {
  const anchor = useRef(null);
  const panel = useRef(null);
  const downloadUrl = useRef("");
  const [downloaded, setDownloaded] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const background = anchor.current?.closest(".sow-workspace-shell");
    const wasInert = background?.hasAttribute("inert");
    background?.setAttribute("inert", "");
    panel.current?.focus();
    const warnBeforeUnload = event => { event.preventDefault(); event.returnValue = ""; };
    window.addEventListener("beforeunload", warnBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", warnBeforeUnload);
      if (!wasInert) background?.removeAttribute("inert");
      if (downloadUrl.current) URL.revokeObjectURL(downloadUrl.current);
    };
  }, []);

  const download = () => {
    try {
      if (downloadUrl.current) URL.revokeObjectURL(downloadUrl.current);
      downloadUrl.current = URL.createObjectURL(new Blob([recovery.json], { type: "application/json" }));
      const link = document.createElement("a");
      link.href = downloadUrl.current;
      link.download = `${projectName.replace(/[^a-z0-9-]/gi, '-')}-plan-recovery.json`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setDownloaded(true);
      setError("");
    } catch {
      setError("The download could not start. Your draft is still available here.");
    }
  };
  const trapFocus = event => {
    if (event.key !== "Tab") return;
    const buttons = [...panel.current.querySelectorAll("button:not(:disabled)")];
    const first = buttons[0], last = buttons[buttons.length - 1];
    if (event.shiftKey && (document.activeElement === first || document.activeElement === panel.current)) {
      event.preventDefault(); last?.focus();
    } else if (!event.shiftKey && (document.activeElement === last || document.activeElement === panel.current)) {
      event.preventDefault(); first?.focus();
    }
  };

  return <><span ref={anchor} hidden />{createPortal(
    <section className="plan-recovery" role="dialog" aria-modal="true" aria-labelledby="plan-recovery-title"
      ref={panel} tabIndex={-1} onKeyDown={trapFocus}>
      <div className="plan-recovery__content">
        <h1 id="plan-recovery-title">Your Idea Builder session ended</h1>
        <p>Salesforce access is closed. No further changes will be sent.</p>
        <p role="status">{recovery.phase === "capturing" ? "Capturing your open draft..."
          : recovery.phase === "failed" ? recovery.error
            : downloaded ? "Draft download started. Check the file before clearing this draft."
              : "Your draft is available to download. It has not been confirmed saved in Salesforce."}</p>
        {error && <p role="alert">{error}</p>}
        <div className="plan-recovery__actions">
          {recovery.phase === "ready" && <button type="button" className="plan-recovery__download" onClick={download}><FiDownload aria-hidden="true" />Download draft</button>}
          {recovery.phase === "failed" && <button type="button" onClick={onRetry}><FiRefreshCw aria-hidden="true" />Retry draft capture</button>}
          <button type="button" onClick={() => {
            if (window.confirm("Clear this draft from the browser and return to sign in? Download it first to keep a copy.")) onClear();
          }}><FiTrash2 aria-hidden="true" />Clear draft and sign in</button>
          <button type="button" onClick={onLogout}><FiLogOut aria-hidden="true" />Sign out and clear draft</button>
        </div>
      </div>
    </section>, document.body,
  )}</>;
}
